// ─── HTML Escaping ──────────────────────────────────────────────────────────

const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

const HTML_UNESCAPE_REGEX = /[&<>"']/g;

/**
 * Safely escapes characters that could lead to XSS when rendering HTML strings.
 * Returns an empty string for null, undefined, or empty values.
 */
export function escapeHtml(input: unknown): string {
  if (input === null || input === undefined) {
    return "";
  }
  const str = String(input);
  return str.replace(HTML_UNESCAPE_REGEX, (char) => HTML_ESCAPE_MAP[char] || char);
}

// ─── URL Protocol & Scheme Validation ───────────────────────────────────────

export const DEFAULT_ALLOWED_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"] as const;

/**
 * Checks if a URL uses a safe protocol (http, https, mailto, tel).
 * Specifically rejects javascript:, data:, vbscript:, file:, etc.
 */
export function isSafeUrl(
  url: unknown,
  allowedProtocols: readonly string[] = DEFAULT_ALLOWED_PROTOCOLS
): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  // Handle relative paths (e.g. /cv, /download) safely
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return true;
  }

  // Reject dangerous pseudo-protocols even with leading whitespace or encoding
  const sanitized = trimmed.replace(/[\x00-\x1F\x7F-\x9F]/g, "").toLowerCase();
  if (
    sanitized.startsWith("javascript:") ||
    sanitized.startsWith("data:") ||
    sanitized.startsWith("vbscript:") ||
    sanitized.startsWith("file:")
  ) {
    return false;
  }

  try {
    const parsed = new URL(trimmed);
    return allowedProtocols.includes(parsed.protocol.toLowerCase());
  } catch {
    // If URL parsing fails and it's not a safe relative URL, it's unsafe
    return false;
  }
}

/**
 * Sanitizes an untrusted URL. Returns the escaped URL if safe, or a fallback (default empty string).
 */
export function sanitizeUrl(
  url: unknown,
  fallback = "",
  allowedProtocols: readonly string[] = DEFAULT_ALLOWED_PROTOCOLS
): string {
  if (!isSafeUrl(url, allowedProtocols)) {
    return fallback;
  }
  return escapeHtml(url);
}

// ─── Reserved Subdomains ─────────────────────────────────────────────────────

export const RESERVED_SUBDOMAINS = new Set([
  "admin",
  "administrator",
  "about",
  "api",
  "app",
  "apps",
  "assets",
  "auth",
  "billing",
  "blog",
  "cdn",
  "contact",
  "cv",
  "dashboard",
  "dev",
  "developer",
  "docs",
  "faq",
  "features",
  "help",
  "jobs",
  "login",
  "mail",
  "media",
  "oauth",
  "pay",
  "payment",
  "portal",
  "preview",
  "pricing",
  "privacy",
  "profolio",
  "register",
  "renderer",
  "root",
  "signin",
  "signup",
  "staging",
  "static",
  "status",
  "support",
  "terms",
  "test",
  "user",
  "users",
  "www",
  "web",
]);

/**
 * Checks if a given subdomain is reserved by the platform.
 */
export function isReservedSubdomain(subdomain: string): boolean {
  if (!subdomain) return true;
  const normalized = subdomain.trim().toLowerCase();
  return RESERVED_SUBDOMAINS.has(normalized);
}

export const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
export const SUBDOMAIN_MIN_LENGTH = 3;
export const SUBDOMAIN_MAX_LENGTH = 63;

/**
 * Validates that a subdomain matches formatting requirements:
 * 3-63 characters, lowercase alphanumeric and hyphens (not starting/ending with hyphen).
 */
export function isValidSubdomainFormat(subdomain: string): boolean {
  if (!subdomain || typeof subdomain !== "string") return false;
  const trimmed = subdomain.trim();
  if (trimmed.length < SUBDOMAIN_MIN_LENGTH || trimmed.length > SUBDOMAIN_MAX_LENGTH) {
    return false;
  }
  return SUBDOMAIN_REGEX.test(trimmed);
}

/**
 * Normalizes a subdomain string: trims and lowercases.
 */
export function normalizeSubdomain(subdomain: string): string {
  return subdomain.trim().toLowerCase();
}

export * from './markdown';
export { sanitizeCustomCss, scopeCustomCss } from './css-sanitizer';
